import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Image, Popup, Modal, Button, Header } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../actions/authActions";
import { useMsal } from "@azure/msal-react";
import { fetchOrganizationDetails } from "../actions/adminConsoleAction";
import { createQuickChatDataset } from "../services/quickChatService";
import "../styles/UserNavbar.css";
import { fontWeight } from "@mui/system";

const NavItem = ({ item, activeMenu }) => {
  const [isOpen, setIsOpen] = useState(() => {
    const savedState = localStorage.getItem(`navItem-${item.text}`);
    return savedState ? JSON.parse(savedState) : false;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserType = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (item.text === activeMenu || location.pathname === "/") {
      setIsOpen(true);
    }
  }, [activeMenu, item.text, location.pathname]);

  useEffect(() => {
    localStorage.setItem(`navItem-${item.text}`, JSON.stringify(isOpen));
  }, [isOpen]);

  const handleToggle = async (e) => {
    if (item.submenu) {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (item.isQuickChat) {
      e.preventDefault();
      
      // Navigate immediately to show loading state
      navigate(`/aiassistant?quickChat=1&method=quickChat&loading=true`);
      
      // Create dataset in background
      try {
        const { datasetId, datasetName } = await createQuickChatDataset('quickChat');
        // Update URL with dataset info once created
        navigate(`/aiassistant?c=${datasetId}&quickChat=1&method=quickChat&title=${datasetName}&success=quickChat`);
      } catch (error) {
        console.error('Error creating quick chat:', error);
        // On error, go back to main AI Assistant page
        navigate('/aiassistant');
      }
    }
  };

  const isSubActive = (subitem) => {
    return location.pathname.includes(subitem.link);
  };

  const isActive = () => {
    if (item.submenu && item.submenu.length > 0) {
      return false; // Do not highlight the parent item
    }

    // Check for Quick Chat mode
    const urlParams = new URLSearchParams(location.search);
    const quickChatParam = urlParams.get("quickChat");
    const methodParam = urlParams.get("method");
    const loadingParam = urlParams.get("loading");
    const isInQuickChatMode = quickChatParam === "1" && methodParam === "quickChat";

    // If this is the Quick Chat item and we're in quick chat mode (including loading), highlight it
    if (item.isQuickChat && isInQuickChatMode) {
      return true;
    }

    // If this is AI Assistant item and we're in quick chat mode (including loading), don't highlight it
    if (item.text === "AI Assistant" && isInQuickChatMode) {
      return false;
    }

    const linkPath = item.link;
    if (linkPath === "/dataconsole" && location.pathname === "/") {
      return false;
    } else if (
      linkPath === "/aiassistant" &&
      location.pathname === "/" &&
      (currentUserType === "User" || currentUserType === "HumanAgent")
    ) {
      return true;
    } else {
      return location.pathname.includes(linkPath);
    }
  };

  return (
    <li
      className={`${isActive() ? "active" : ""} ${
        item.submenu ? "has-submenu" : ""
      }`}
    >
      <Link to={item.link} onClick={handleToggle} data-cy={item.cy}>
        {item.icon && typeof item.icon === "string" && (
          <Image
            src={item.icon}
            alt="Icon"
            className="icons"
            style={{ border: "none" }}
          />
        )}
        {item.tooltip ? (
          <Popup
            content={item.tooltip}
            inverted
            position="right center"
            style={{ borderRadius: "15px" }}
            trigger={<span className="title">{item.text}</span>}
          />
        ) : (
          <span className="title">{item.text}</span>
        )}
        {item.submenu && (
          <span className={`submenu-chevron ${isOpen ? "open" : ""}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </Link>
      {item.submenu && isOpen && (
        <ul className="submenu">
          {item.submenu.map((subitem) => (
            <li
              className={isSubActive(subitem) ? "active" : ""}
              key={subitem.text}
            >
              <Link to={subitem.link}>
                {subitem.icon && typeof subitem.icon === "string" && (
                  <Image src={subitem.icon} alt="Subitem Icon" className="icons" />
                )}
                <span className="title">{subitem.text}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Sidebar = ({ toggleBtn, onCloseButtonClick, activeMenu }) => {
  const [isCollapse, setisCollapse] = useState(true);
  const dispatch = useDispatch();
  const [userName, setusername] = useState("");
  const [userEmail, setuserEmail] = useState("");
  const [quickChatLabel, setQuickChatLabel] = useState("");
  const currentUserType = useSelector((state) => state.auth.user);
  const userToken = useSelector((state) => state.auth.token);
  const orgDetails = useSelector((state) => state.admin.details);
  const { instance } = useMsal();

  const [isProfilePopupVisible, setProfilePopupVisible] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const profilePopupRef = useRef(null);
  const userProfileBoxRef = useRef(null);
  const navigate = useNavigate();

  const toggleProfilePopup = () => {
    setProfilePopupVisible(!isProfilePopupVisible);
  };

  const handleClickOutside = (event) => {
    if (
      profilePopupRef.current &&
      !profilePopupRef.current.contains(event.target) &&
      !userProfileBoxRef.current.contains(event.target)
    ) {
      setProfilePopupVisible(false);
    }
  };

  const handleCloseSidebar = () => {
    setisCollapse(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    if (onCloseButtonClick) {
      onCloseButtonClick();
    }
    setProfilePopupVisible(false);
    // Open the logout confirmation modal
    setLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    instance.logoutRedirect();
    setLogoutModalOpen(false);
  };

  const cancelLogout = () => {
    setLogoutModalOpen(false);
  };

  useEffect(() => {
    if (userToken) {
      setusername(userToken.name);
      setuserEmail(userToken.extension_UserEmail);
    }
  }, [userToken]);

  useEffect(() => {
    dispatch(fetchOrganizationDetails());
  }, [dispatch]);

  useEffect(() => {
    // Set QuickChat label from org details
    if (orgDetails?.orgName) {
      const defaultLabel = `${orgDetails.orgName} Assistant`;
      setQuickChatLabel(orgDetails.quickChatLabel || defaultLabel);
    }
  }, [orgDetails]);

  const generateUserNav = (userType) => {
    const fullOrgName = orgDetails?.orgName || "Innover";
    const isQuickChatDisabled = orgDetails?.quickChatDisabled || false;
    
    let assistantLabel, assistantTooltip;
    
    if (quickChatLabel) {
      // Check if custom label matches default format
      const defaultLabel = `${fullOrgName} Assistant`;
      
      if (quickChatLabel === defaultLabel) {
        // Same as default: truncate org name if > 6 characters, keep "Assistant"
        if (fullOrgName.length > 6) {
          const truncatedOrgName = fullOrgName.substring(0, 6) + "...";
          assistantLabel = `${truncatedOrgName} Assistant`;
          assistantTooltip = `${fullOrgName} Assistant`;
        } else {
          assistantLabel = `${fullOrgName} Assistant`;
          assistantTooltip = null;
        }
      } else {
        // Custom label case: check if ends with "Assistant" and handle truncation
        if (quickChatLabel.endsWith(" Assistant")) {
          // Only truncate if length > 18 characters (give some buffer for "Assistant" labels)
          if (quickChatLabel.length > 18) {
            const beforeAssistant = quickChatLabel.substring(0, quickChatLabel.length - " Assistant".length);
            // Try to fit as much as possible in ~15 chars total, use 6 chars + "..." for the prefix
            const truncatedBefore = beforeAssistant.substring(0, 6) + "...";
            assistantLabel = `${truncatedBefore} Assistant`;
            assistantTooltip = quickChatLabel;
          } else {
            // Don't truncate if it's reasonably short
            assistantLabel = quickChatLabel;
            assistantTooltip = null;
          }
        } else if (quickChatLabel.length > 15) {
          // Standard truncation for labels that don't end with "Assistant"
          assistantLabel = quickChatLabel.substring(0, 15) + "...";
          assistantTooltip = quickChatLabel;
        } else {
          assistantLabel = quickChatLabel;
          assistantTooltip = null;
        }
      }
    } else {
      // Default case: truncate org name if > 6 characters
      if (fullOrgName.length > 6) {
        const truncatedOrgName = fullOrgName.substring(0, 6) + "...";
        assistantLabel = `${truncatedOrgName} Assistant`;
        assistantTooltip = `${fullOrgName} Assistant`;
      } else {
        assistantLabel = `${fullOrgName} Assistant`;
        assistantTooltip = null;
      }
    }

    if (userType === "User") {
      return [
        {
          text: "Dataset Access",
          link: "/data-sets",
          icon: "/images/admin-console-icon.svg",
          cy: "datasets-requests-menu-link",
        },
        {
          text: "AI Assistant",
          link: "/aiassistant",
          icon: "/images/ai-assistant.svg",
          cy: "ai-assistant-menu-link",
        },
      ];
    } else if (userType === "QuickChatUser") {
      return [
        {
          text: assistantLabel,
          link: "#",
          icon: "/images/quick-chat-icon.svg",
          cy: "quick-chat-menu-link",
          isQuickChat: true,
          tooltip: assistantTooltip,
        },
      ];
    } else if (userType === "Admin" || userType === "OrgAdmin" || userType === "DatasetAdmin") {
      const nav = [
        {
          text: "Admin Console",
          icon: "/images/admin-console-icon.svg",
          cy: "adminconsole-main-menu-link",
          submenu: [
            {
              text: "Groups",
              link: "/adminconsole/groups",
              icon: "/images/group.svg",
              cy: "admin-groups-menu-link",
            },
            {
              text: "All Users",
              link: "/adminconsole/allusers",
              icon: "/images/users.svg",
            },
            {
              text: "Requests",
              link: "/adminconsole/requests",
              icon: "/images/user.svg",
              cy: "datasets-requests-menu-link",
            },
          ],
        },
        {
          text: "Data Console",
          link: "/dataconsole",
          icon: "/images/data-console.svg",
          cy: "data-console-menu-link",
        },
        {
          text: "AI Assistant",
          link: "/aiassistant",
          icon: "/images/ai-assistant.svg",
          cy: "ai-assistant-menu-link",
        },
      ];

      // Only add QuickChat if it's enabled  
      if (isQuickChatDisabled) {
        nav.push({
          text: assistantLabel,
          link: "#",
          icon: "/images/quick-chat-icon.svg",
          cy: "quick-chat-menu-link",
          isQuickChat: true,
          tooltip: assistantTooltip,
        });
      }

      nav.push({
        text: "Dashboards",
        icon: "/images/dashboard-title.svg",
        cy: "dashboard-menu-link",
        submenu: [
          {
            text: "Organization",
            link: "/dashboard/orgdashboard",
            icon: "/images/organisation-title.svg",
          },
          {
            text: "Datasets",
            link: "/dashboard/datasetdashboard",
            icon: "/images/rating-title.svg",
          },
          {
            text: "Feedback",
            link: "/dashboard/feedbackdashboard",
            icon: "/images/feedback-title.svg",
          },
          {
            text: "User Engagement",
            link: "/dashboard/engagementdashboard",
            icon: "/images/dashboard.svg",
          },
        ],
      });

      nav.push({
        text: "Usage",
        icon: "/images/dashboard.svg",
        cy: "usage-menu-link",
        submenu: [
          // Token Usage - only for OrgAdmin
          ...(userType === "OrgAdmin" ? [{
            text: "Tokens",
            link: "/dashboard/tokenusage",
            icon: "/images/dashboard.svg",
            cy: "token-usage-menu-link",
          }] : []),
          // Session Usage - for OrgAdmin, Admin, and DatasetAdmin
          ...(userType === "OrgAdmin" || userType === "Admin" || userType === "DatasetAdmin" ? [{
            text: "Sessions",
            link: "/dashboard/sessionusage",
            icon: "/images/dashboard.svg",
            cy: "session-usage-menu-link",
          }] : []),
        ].filter(item => item), // Remove empty items
      });
      
      // Only add Usage menu if it has submenu items
      if (nav[nav.length - 1].submenu.length === 0) {
        nav.pop(); // Remove empty Usage menu
      }
      
      return nav;
    } else if (userType === "HumanAgent") {
      return [];
    } else if (userType === "SuperAdmin") {
      return [
        {
          text: "Organizations",
          link: "/organizations",
          icon: "/images/data-console.svg",
          cy: "organisations-menu-link",
        },
      ];
    } else {
      // Handle other user types or default case
      return [];
    }
  };

  const nav = generateUserNav(currentUserType);

  const handleHelpClick = () => {
    window.open("/docs/", "_blank");
  };

  const handleSettingsClick = () => {
    navigate("/settings");
  };

  const handleConfigurationClick = () => {
    navigate("/configuration");
  };

  return (
    <>
      <div
        className={`sidebar ${
          toggleBtn ? "sidebarResponsviveMobile" : "sidebarResponsiveDesktop"
        }`}
      >
        <div onClick={onCloseButtonClick}>
          <Image
            className="sidebarclose"
            src="/images/Close.svg"
            alt="Close Icon"
            data-cy="Close-icon"
          />
        </div>

        {/* Logo at the top (hidden for ExternalUser) */}
        {currentUserType !== "ExternalUser" && (
          <div className="sidebar-logo-section">
            <Image
              src="/images/Logo.png"
              alt="Logo"
              className="logoImage"
            />
          </div>
        )}

        {/* Org Logo & Name (for certain user types) */}
        {(currentUserType === "Admin" ||
          currentUserType === "OrgAdmin" ||
          currentUserType === "User" ||
          currentUserType === "DatasetAdmin" ||
          currentUserType === "QuickChatUser") && (
          <div className="orgContainer">
            <Image
              src={orgDetails?.logoBase64}
              className="circularLogo"
            />
            <span className="orgName">{orgDetails?.orgName}</span>
          </div>
        )}

        {/* Navigation Menu */}
        <div className="navigationIcons">
          <ul>
            {nav.map((item) => (
              <NavItem key={item.text} item={item} activeMenu={activeMenu} />
            ))}
          </ul>
        </div>

        {/* Profile Popup */}
        {isProfilePopupVisible && (
          <>
          <div className="profile-popup-overlay" onClick={toggleProfilePopup} />
          <div ref={profilePopupRef} className="profile-popup">
            {currentUserType === "OrgAdmin" && (
              <div
                className="profile-configuration"
                onClick={handleConfigurationClick}
              >
                <img src="/images/Settings.svg" alt="Configuration" />
                <span>Organization Settings</span>
              </div>
            )}
            <div className="profile-settings" onClick={handleSettingsClick}>
              <img src="/images/faq.svg" alt="Settings" />
              <span>FAQs</span>
            </div>
            <div
              className="profile-help"
              onClick={handleHelpClick}
              data-cy="help-click-cy"
            >
              <img src="/images/Help.svg" alt="Help" />
              <span>Help</span>
            </div>
            <div
              className="profile-logout"
              onClick={handleLogout}
              data-cy="logout-click-cy"
            >
              <img src="/images/log_out.svg" alt="Logout" />
              <span>Logout</span>
            </div>
          </div>
          </>
        )}

        {/* User Profile Box */}
        <div
          ref={userProfileBoxRef}
          className="userProfileBox"
          onClick={toggleProfilePopup}
          data-cy="profile-popup-click-cy"
        >
          <div className="userIcon">
            <svg
              width="18"
              height="20"
              viewBox="0 0 18 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 19V17C17 15.9391 16.5786 14.9217 15.8284 14.1716C15.0783 13.4214 14.0609 13 13 13H5C3.93913 13 2.92172 13.4214 2.17157 14.1716C1.42143 14.9217 1 15.9391 1 17V19M13 5C13 7.20914 11.2091 9 9 9C6.79086 9 5 7.20914 5 5C5 2.79086 6.79086 1 9 1C11.2091 1 13 2.79086 13 5Z"
                stroke="#97A3BA"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="nameEmail">
            <h4>{userName}</h4>
            <Popup
              content={userEmail}
              inverted
              position="right center"
              style={{ borderRadius: "15px" }}
              trigger={<p className="email">{userEmail.slice(0, 14) + "..."}</p>}
            />
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        open={isLogoutModalOpen}
        onClose={cancelLogout}
        size="tiny"
        className="logout-modal"
        mountNode={document.body}
      >
        <Modal.Content>
          <Header as="h3">Logout {userName}</Header>
          <p style={{ textAlign: "center" }}>
            Are you sure you want to log out?
          </p>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={cancelLogout}>Cancel</Button>
          <Button onClick={confirmLogout} primary>
            Logout
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default Sidebar;
